import type { UseWalletUIResult } from 'src/blockchain';

/**
 * On-chain reads for the delivery escrow, on whichever chain the deal settles (contracts v3).
 *
 * The buyer's projection carries display amounts, not the exact base-unit total the contract will
 * pull, and not the token address it settles in. Both are read from the deployed clone so the
 * money path never depends on the client guessing decimals — the same "on-chain truth" principle
 * the rental pay flow follows. Which chain that clone is on is the wallet's problem, not this
 * module's: it asks the wallet of the deal's family, which is why nothing here imports a chain SDK.
 *
 * `platform` is read only for the legacy TRON gas transfer (deal v2 §4.3). v3 deals on Base have
 * no gas line at all — the platform absorbs the deploy — so nothing there needs the address.
 */

/** The views this module reads, with their return types (the wallet builds the ABI from these). */
const TOTAL_REQUIRED = 'getTotalRequired() view returns (uint256)';
const PAYMENT_TOKEN = 'paymentToken() view returns (address)';
const PLATFORM = 'platform() view returns (address)';

export interface DeliveryContractInfo {
  /** price + delivery + fee in token base units (what fund() pulls), as a decimal string. */
  totalRequired: string;
  /** The token the escrow settles in — the exact token to approve. */
  paymentToken: string;
  /** Platform (fee-wallet) address — the native-TRX gas transfer target (deal v2 §4.3). */
  platform: string;
}

/**
 * Read the fund total, the settlement token and the platform address from a deployed clone.
 *
 * A single retry absorbs read-after-deploy eventual consistency, which both chains have and both
 * express as nonsense rather than an error: a fresh clone answers zeros on TRON and reverts on
 * Base ([[nile-trongrid-eventual-consistency-reads]]). A persistent failure surfaces so the buyer
 * retries, rather than signing against an amount nobody verified.
 */
export async function readDeliveryContractInfo(
  walletUI: Pick<UseWalletUIResult, 'readContract'>,
  contractAddress: string,
  chainId?: number
): Promise<DeliveryContractInfo> {
  const read = async (): Promise<DeliveryContractInfo> => {
    const [totalRequired, paymentToken, platform] = await walletUI.readContract({
      contractAddress,
      signatures: [TOTAL_REQUIRED, PAYMENT_TOKEN, PLATFORM],
      chainId,
    });

    if (!totalRequired || totalRequired === '0' || !paymentToken || !platform) {
      throw new Error('Delivery contract is not ready yet');
    }

    return { totalRequired, paymentToken, platform };
  };

  try {
    return await read();
  } catch {
    await new Promise((resolve) => setTimeout(resolve, 3000));

    return read();
  }
}
